// src/ConfirmareOre.js
import * as React from "react";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDataProvider, useNotify } from "react-admin";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import { getColaboratorId } from "./usePermissions";
import { fetchProgramEveniment } from "./programEveniment";

const extractId = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value.id;
    if (typeof value === "string") {
        const match = value.match(/\/(\d+)$/);
        return match ? parseInt(match[1], 10) : null;
    }
    return value;
};

const extractIds = (collection) => (collection || []).map(extractId).filter(Boolean);

// Ziua calendaristica (YYYY-MM-DD) a unui rand eveniment_ore. Coloana `data` e
// de tip `date` in Doctrine, iar API-ul o trimite ca sir care INCEPE cu ziua -
// luam prefixul direct, fara new Date(), pentru ca parsarea locala ar putea
// muta ziua cu una in fusurile negative.
const ziuaIso = (value) => {
    if (!value) return null;
    if (typeof value === "string") {
        const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
        if (m) return m[1];
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const luna = String(d.getUTCMonth() + 1).padStart(2, "0");
    const zi = String(d.getUTCDate()).padStart(2, "0");
    return `${d.getUTCFullYear()}-${luna}-${zi}`;
};

// Formatare in fusul TENANTULUI (primit de la API), nu al browserului: ziua de
// munca apartine locului muncii.
const formatInFus = (value, fusOrar, optiuni) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    try {
        return new Intl.DateTimeFormat("ro-RO", { timeZone: fusOrar || undefined, ...optiuni }).format(d);
    } catch (e) {
        // fus invalid / nesuportat de browser -> cadem pe fusul local
        return new Intl.DateTimeFormat("ro-RO", optiuni).format(d);
    }
};

const formatDataOra = (value, fusOrar) =>
    formatInFus(value, fusOrar, {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });

// Etichetele zilelor se construiesc din sirul YYYY-MM-DD venit de la backend
// (deja calculat in fusul tenantului). Fixam ora la pranz UTC si formatam in
// UTC, ca sa nu alunece ziua la conversie.
const etichetaZi = (ziIso, cuAn = false) => {
    if (!ziIso) return "";
    const d = new Date(`${ziIso}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return ziIso;
    return new Intl.DateTimeFormat("ro-RO", {
        timeZone: "UTC",
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        ...(cuAn ? { year: "numeric" } : {}),
    }).format(d);
};

// Verifica daca un colaborator are unul dintre rolurile date (case-insensitive)
const areVreunulDinRoluri = (colaborator, roluriCautate) => {
    const roluriColab = (colaborator.rol || "")
        .toLowerCase()
        .split(",")
        .map((r) => r.trim());
    return roluriCautate.some((r) => roluriColab.includes(r));
};

const esteAdmin = (colaborator) => areVreunulDinRoluri(colaborator, ["admin"]);
const esteResponsabil = (colaborator) => areVreunulDinRoluri(colaborator, ["responsabil"]);

const ConfirmareOreBase = ({ eveniment }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();

    // id-ul colaboratorului logat curent (din JWT, via usePermissions)
    const colaboratorLogatId = getColaboratorId();

    // colaboratori din echipele asociate evenimentului - universul complet
    // de oameni care POT avea ore pe acest eveniment
    const [colaboratori, setColaboratori] = useState([]);
    // toti colaboratorii din tenant - folositi pentru dropdown-ul "Confirmat de"
    const [toatiColaboratorii, setToatiColaboratorii] = useState([]);
    const [echipeMap, setEchipeMap] = useState({});
    const [confirmariExistente, setConfirmariExistente] = useState([]);
    // Stare PE ZI: oreMap[zi][colaboratorId], detaliiMap[zi][colaboratorId]
    const [oreMap, setOreMap] = useState({});
    const [detaliiMap, setDetaliiMap] = useState({});
    const [ziActiva, setZiActiva] = useState(null);
    // Programul dedus de backend (sursa de adevar pentru zile si orele/zi). Cat
    // timp e null, inca se incarca.
    const [program, setProgram] = useState(null);
    const [programLoading, setProgramLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [oreGlobale, setOreGlobale] = useState("");
    const [confirmatDeId, setConfirmatDeId] = useState("");
    // marcheaza daca auto-selectia initiala a fost deja facuta, ca sa nu
    // suprascriem alegerea manuala a userului la fiecare reload() din load()
    const [autoSelectFacut, setAutoSelectFacut] = useState(false);

    // Focus pe titlul sectiunii o singura data, la montare. Un ref inline
    // (el) => el.focus() ar refocaliza la FIECARE render - adica la fiecare
    // tasta apasata in dialogul de editare - si ar fura cursorul din campuri.
    const tituRef = useRef(null);
    useEffect(() => {
        tituRef.current?.focus();
    }, []);

    // Orele/zi si zilele vin din backend, nu mai sunt calculate local.
    const oreImplicite = program?.orePeZi ?? 0;
    const fusOrar = program?.fusOrar || null;
    const zile = useMemo(() => program?.zile ?? [], [program]);
    // Cheie stabila pentru dependinte: `zile` e un array nou la fiecare fetch.
    const zileKey = useMemo(
        () => zile.map((z) => `${z.data}:${z.orePeZi}`).join("|"),
        [zile]
    );
    const totalOreEveniment = useMemo(
        () => zile.reduce((s, z) => s + (Number(z.orePeZi) || 0), 0),
        [zile]
    );
    const echipeIds = extractIds(eveniment?.echipe);

    useEffect(() => {
        let activ = true;
        if (!eveniment?.id) { setProgram(null); setProgramLoading(false); return; }
        setProgramLoading(true);
        fetchProgramEveniment(eveniment.id)
            .then((p) => { if (activ) setProgram(p); })
            .catch(() => { if (activ) setProgram(null); })
            .finally(() => { if (activ) setProgramLoading(false); });
        return () => { activ = false; };
        // dataStart/dataEnd in deps: daca se editeaza intervalul, reincarcam.
    }, [eveniment?.id, eveniment?.dataStart, eveniment?.dataEnd]);

    // Regula de deblocare (simpla, deliberat): daca evenimentul a inceput, TOATE
    // zilele lui sunt confirmabile. Deblocarea per-zi la ora de final dedusa e
    // un pas ulterior - endpoint-ul de program nu intoarce inca orele de
    // inceput/final pe zi.
    const evenimentInceput = eveniment?.dataStart
        ? new Date(eveniment.dataStart) <= new Date()
        : false;
    const evenimentTerminat = eveniment?.dataEnd
        ? new Date(eveniment.dataEnd) <= new Date()
        : false;

    // Campul "Aplica la toti" porneste de la orele/zi deduse de server.
    useEffect(() => {
        if (program?.orePeZi != null) setOreGlobale(String(program.orePeZi));
    }, [program?.orePeZi]);

    const load = useCallback(async () => {
        if (!eveniment?.id || !echipeIds.length) {
            setColaboratori([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const [colabRes, oreRes, echipeRes] = await Promise.all([
                dataProvider.getList("colaboratori", {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: "id", order: "ASC" },
                    filter: {},
                }),
                dataProvider.getList("evenimente-ore", {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: "id", order: "ASC" },
                    filter: {},
                }),
                dataProvider.getList("echipe", {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: "id", order: "ASC" },
                    filter: {},
                }),
            ]);

            setEchipeMap(Object.fromEntries(echipeRes.data.map((e) => [e.id, e.nume])));

            setToatiColaboratorii(colabRes.data);

            const colaboratoriEchipa = colabRes.data.filter((c) =>
                echipeIds.includes(extractId(c.echipa))
            );
            setColaboratori(colaboratoriEchipa);

            const confirmariEveniment = oreRes.data.filter(
                // eslint-disable-next-line eqeqeq
                (o) => extractId(o.eveniment) == eveniment.id
            );
            setConfirmariExistente(confirmariEveniment);

            // Auto-selectie: daca userul logat are rol responsabil/admin si
            // exista in lista de colaboratori, il pre-selectam automat ca
            // "Confirmat de" - se face o singura data, ca sa nu suprascriem
            // o eventuala alegere manuala ulterioara la reload-uri din save.
            if (!autoSelectFacut && colaboratorLogatId) {
                const userulLogat = colabRes.data.find((c) => c.id === colaboratorLogatId);
                if (userulLogat && (esteAdmin(userulLogat) || esteResponsabil(userulLogat))) {
                    setConfirmatDeId(userulLogat.id);
                }
                setAutoSelectFacut(true);
            }
        } catch (err) {
            notify("Eroare la incarcarea colaboratorilor/orelor", { type: "error" });
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eveniment?.id, JSON.stringify(echipeIds)]);

    useEffect(() => {
        load();
    }, [load]);

    // Construirea starii per-zi e separata de load(): depinde si de program
    // (zilele), care vine pe alt canal. Ruleaza doar cand se schimba datele
    // incarcate (montare, salvare, editarea intervalului) - NU la tastare, deci
    // nu inghite ce scrie userul.
    useEffect(() => {
        if (!zile.length || !colaboratori.length) return;
        const ore = {};
        const detalii = {};
        zile.forEach((z) => {
            const zi = z.data;
            const confirmariZi = confirmariExistente.filter((o) => ziuaIso(o.data) === zi);
            // Daca ziua asta are deja ore logate, cine lipseste primeste 0
            // (a lipsit), nu orele implicite.
            const ziAreOreLogate = confirmariZi.length > 0;
            const implicitZi = String(z.orePeZi ?? oreImplicite);
            ore[zi] = {};
            detalii[zi] = {};
            colaboratori.forEach((c) => {
                const existing = confirmariZi.find((o) => extractId(o.colaborator) === c.id);
                ore[zi][c.id] = existing ? String(existing.oreConfirmate) : (ziAreOreLogate ? "0" : implicitZi);
                detalii[zi][c.id] = existing?.detalii || "";
            });
        });
        setOreMap(ore);
        setDetaliiMap(detalii);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colaboratori, confirmariExistente, zileKey, oreImplicite]);

    // Zilele care au deja cel putin o confirmare - pentru bifa din tab.
    const zileConfirmate = useMemo(() => {
        const set = new Set();
        confirmariExistente.forEach((o) => {
            const zi = ziuaIso(o.data);
            if (zi) set.add(zi);
        });
        return set;
    }, [confirmariExistente]);

    // Ziua activa: prima zi neconfirmata, altfel prima zi.
    useEffect(() => {
        if (!zile.length) { setZiActiva(null); return; }
        const exista = zile.some((z) => z.data === ziActiva);
        if (exista) return;
        const primaNeconfirmata = zile.find((z) => !zileConfirmate.has(z.data));
        setZiActiva((primaNeconfirmata || zile[0]).data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zileKey, zileConfirmate]);

    // Responsabili eligibili pentru dropdown-ul "Confirmat de"
    const responsabili = useMemo(
        () => toatiColaboratorii.filter((c) => esteAdmin(c) || esteResponsabil(c)),
        [toatiColaboratorii]
    );

    // Persoana selectata curent (auto sau manual)
    const confirmatDe = useMemo(
        () => toatiColaboratorii.find((c) => c.id === confirmatDeId) || null,
        [toatiColaboratorii, confirmatDeId]
    );

    // Userul logat curent, daca exista in lista de colaboratori
    const userulLogat = useMemo(
        () => toatiColaboratorii.find((c) => c.id === colaboratorLogatId) || null,
        [toatiColaboratorii, colaboratorLogatId]
    );

    // Userul logat poate confirma ore (e admin sau responsabil)?
    const userulPoateConfirma = userulLogat && (esteAdmin(userulLogat) || esteResponsabil(userulLogat));

    // Doar adminii pot schimba manual cine confirma (re-atribuire); un
    // responsabil simplu e mereu fixat pe sine, fara optiunea de a alege
    // pe altcineva (logica de autoritate ramane corecta: el oricum nu ar
    // putea confirma decat propria echipa).
    const poateSchimbaSelectia = userulLogat ? esteAdmin(userulLogat) : false;

    // Domeniul de autoritate al persoanei selectate:
    // - admin -> poate confirma pentru orice colaborator asociat evenimentului
    // - responsabil (fara admin) -> poate confirma DOAR colegii din propria echipa
    const colaboratoriPermisi = useMemo(() => {
        if (!confirmatDe) return [];
        if (esteAdmin(confirmatDe)) {
            return colaboratori;
        }
        const echipaConfirmatDe = extractId(confirmatDe.echipa);
        return colaboratori.filter((c) => extractId(c.echipa) === echipaConfirmatDe);
    }, [confirmatDe, colaboratori]);

    const colaboratoriPermisiIds = useMemo(
        () => new Set(colaboratoriPermisi.map((c) => c.id)),
        [colaboratoriPermisi]
    );

    const handleOreChange = (zi, colaboratorId, value) => {
        setOreMap((prev) => ({ ...prev, [zi]: { ...(prev[zi] || {}), [colaboratorId]: value } }));
    };

    const handleDetaliiChange = (zi, colaboratorId, value) => {
        setDetaliiMap((prev) => ({ ...prev, [zi]: { ...(prev[zi] || {}), [colaboratorId]: value } }));
    };

    // Campul contine ORELE/ZI; butonul le pune la fiecare colaborator permis pe
    // FIECARE zi a evenimentului.
    const aplicaOreGlobale = () => {
        if (oreGlobale === "" || isNaN(parseFloat(oreGlobale))) {
            notify("Introdu un numar valid de ore", { type: "warning" });
            return;
        }
        setOreMap((prev) => {
            const urmator = { ...prev };
            zile.forEach((z) => {
                const zi = z.data;
                const alZilei = { ...(urmator[zi] || {}) };
                colaboratoriPermisi.forEach((c) => { alZilei[c.id] = oreGlobale; });
                urmator[zi] = alZilei;
            });
            return urmator;
        });
    };

    // Scrie orele pentru zilele date. Folosit si pentru o singura zi (butonul
    // din tab), si pentru toate (confirmarea in masa).
    const salveazaZile = async (zileDeSalvat) => {
        if (!confirmatDeId) {
            notify("Selecteaza responsabilul care confirma", { type: "warning" });
            return;
        }
        if (!colaboratoriPermisi.length) {
            notify("Nu ai colaboratori pe care sa-i poti confirma pe acest eveniment", { type: "warning" });
            return;
        }
        if (!zileDeSalvat.length || saving) return;

        setSaving(true);
        try {
            const { data: oreProaspete } = await dataProvider.getList("evenimente-ore", {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: "id", order: "ASC" },
                filter: {},
            });
            const confirmariProaspete = oreProaspete.filter(
                (o) => extractId(o.eveniment) === eveniment.id
            );

            const acum = new Date().toISOString();

            // Secvential pe zile (ca sa nu trimitem zeci de cereri deodata),
            // paralel in interiorul unei zile.
            for (const zi of zileDeSalvat) {
                // eslint-disable-next-line no-await-in-loop
                await Promise.all(
                    colaboratoriPermisi.map(async (c) => {
                        const ore = parseFloat(oreMap[zi]?.[c.id]);
                        if (isNaN(ore)) return;

                        const existing = confirmariProaspete.find(
                            (o) => extractId(o.colaborator) === c.id && ziuaIso(o.data) === zi
                        );

                        const payload = {
                            eveniment: `/api/evenimente/${eveniment.id}`,
                            colaborator: `/api/colaboratori/${c.id}`,
                            data: zi,
                            oreConfirmate: ore,
                            detalii: (detaliiMap[zi]?.[c.id] || "").trim() || null,
                            confirmatDe: `/api/colaboratori/${confirmatDeId}`,
                            confirmatLa: acum,
                        };

                        if (existing) {
                            await dataProvider.update("evenimente-ore", {
                                id: existing.id,
                                data: payload,
                                previousData: existing,
                            });
                        } else {
                            await dataProvider.create("evenimente-ore", { data: payload });
                        }
                    })
                );
            }

            notify(
                zileDeSalvat.length === 1
                    ? `Ore confirmate pentru ${etichetaZi(zileDeSalvat[0])}`
                    : `Ore confirmate pentru ${zileDeSalvat.length} zile`,
                { type: "success" }
            );
            await load();
        } catch (err) {
            const msgBrut = (err.message || "").toLowerCase();
            const esteConflict = msgBrut.includes("unique") || msgBrut.includes("duplicate") || msgBrut.includes("23000");
            notify(
                esteConflict
                    ? "Exista deja o confirmare pentru acest colaborator in ziua respectiva - datele au fost reincarcate"
                    : "Eroare la confirmarea orelor: " + (err.message || ""),
                { type: "error" }
            );
            await load();
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmaZiua = () => salveazaZile(ziActiva ? [ziActiva] : []);
    const handleConfirmaTot = () => salveazaZile(zile.map((z) => z.data));

    if (!echipeIds.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                Asociaza cel putin o echipa evenimentului pentru a confirma orele lucrate.
            </Typography>
        );
    }

    if (!evenimentInceput) {
        return (
            <Alert severity="info">
                Confirmarea orelor va fi disponibila de la inceputul evenimentului
                ({formatDataOra(eveniment.dataStart, fusOrar)}). Nu poti confirma ore pentru un eveniment
                care nu a inceput inca.
            </Alert>
        );
    }

    if (loading || programLoading) {
        return <Typography variant="body2">Se incarca...</Typography>;
    }

    if (!colaboratori.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                Echipele asociate nu au colaboratori.
            </Typography>
        );
    }

    if (!zile.length) {
        return (
            <Alert severity="warning">
                Nu s-a putut determina programul evenimentului (interval incomplet sau server
                indisponibil). Reincarca pagina sau verifica datele evenimentului.
            </Alert>
        );
    }

    // Antetul (interval + total ore) e read-only si se afiseaza si userilor
    // fara drept de confirmare.
    const antet = (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatDataOra(eveniment.dataStart, fusOrar)} – {formatDataOra(eveniment.dataEnd, fusOrar)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {oreImplicite}h/zi × {zile.length} {zile.length === 1 ? "zi" : "zile"} = <strong>{totalOreEveniment}h</strong> total
                {fusOrar ? ` · ore afisate in ${fusOrar}` : ""}
            </Typography>
        </Paper>
    );

    // Userul logat nu are rol responsabil/admin -> nu poate confirma orele
    // altora, dar isi poate confirma propria prezenta, pe fiecare zi.
    if (!userulPoateConfirma) {
        const esteInEchipa = colaboratori.some((c) => c.id === colaboratorLogatId);
        const colabLogat = colaboratori.find((c) => c.id === colaboratorLogatId);
        const numeEchipa = colabLogat ? (echipeMap[extractId(colabLogat.echipa)] || "") : "";

        const handleConfirmaPrezenta = async (zi, orePeZiZi) => {
            setSaving(true);
            try {
                await dataProvider.create("evenimente-ore", {
                    data: {
                        eveniment: `/api/evenimente/${eveniment.id}`,
                        colaborator: `/api/colaboratori/${colaboratorLogatId}`,
                        data: zi,
                        oreConfirmate: orePeZiZi ?? oreImplicite ?? 0,
                        confirmatDe: `/api/colaboratori/${colaboratorLogatId}`,
                        confirmatLa: new Date().toISOString(),
                    },
                });
                notify(`Prezenta confirmata pentru ${etichetaZi(zi)}`, { type: "success" });
                await load();
            } catch (err) {
                notify("Eroare la confirmarea prezentei", { type: "error" });
            } finally {
                setSaving(false);
            }
        };

        return (
            <Box>
                {antet}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Nu ai dreptul de a confirma ore pe acest eveniment.
                </Typography>
                {esteInEchipa && zile.map((z) => {
                    const areDejaConfirmat = confirmariExistente.some(
                        (o) => extractId(o.colaborator) === colaboratorLogatId && ziuaIso(o.data) === z.data
                    );
                    return (
                        <Box key={z.data} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <Typography variant="body2" sx={{ minWidth: 130 }}>
                                {etichetaZi(z.data)}
                            </Typography>
                            {areDejaConfirmat ? (
                                <Typography variant="body2" color="success.main">
                                    ✅ Prezenta confirmata{numeEchipa ? ` (${numeEchipa})` : ""}
                                </Typography>
                            ) : (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={() => handleConfirmaPrezenta(z.data, z.orePeZi)}
                                    disabled={saving}
                                >
                                    Confirma prezenta ({z.orePeZi ?? oreImplicite}h)
                                </Button>
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    }

    const oreZiActiva = oreMap[ziActiva] || {};
    const detaliiZiActiva = detaliiMap[ziActiva] || {};
    const confirmariZiActiva = confirmariExistente.filter((o) => ziuaIso(o.data) === ziActiva);

    return (
        <Box>
            <Typography
                variant="subtitle2"
                sx={{ mb: 1 }}
                tabIndex={-1}
                ref={tituRef}
            >
                Confirmare ore lucrate
            </Typography>

            {antet}

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
                {poateSchimbaSelectia ? (
                    <FormControl size="small" sx={{ minWidth: 220 }} disabled={!responsabili.length || saving}>
                        <InputLabel>Confirmat de (responsabil)</InputLabel>
                        <Select
                            value={confirmatDeId}
                            label="Confirmat de (responsabil)"
                            onChange={(e) => setConfirmatDeId(e.target.value)}
                        >
                            {responsabili.map((r) => (
                                <MenuItem key={r.id} value={r.id}>
                                    {r.nume} {r.prenume} {esteAdmin(r) ? "(admin)" : "(responsabil)"}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <Typography variant="body2">
                        Confirmi ca: <strong>{userulLogat?.nume} {userulLogat?.prenume}</strong> (responsabil)
                    </Typography>
                )}
            </Box>

            {confirmatDeId && !esteAdmin(confirmatDe) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Poti confirma ore doar pentru colaboratorii din propria echipa.
                </Alert>
            )}

            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
                <TextField
                    size="small"
                    type="number"
                    label="Ore/zi pentru toti"
                    value={oreGlobale}
                    onChange={(e) => setOreGlobale(e.target.value)}
                    sx={{ width: 160 }}
                    disabled={!confirmatDeId || saving}
                    inputProps={{ step: 0.5, min: 0 }}
                />
                <Button size="small" variant="outlined" onClick={aplicaOreGlobale} disabled={!confirmatDeId || saving}>
                    Aplica la toti, pe toate zilele
                </Button>
            </Box>

            <Tabs
                value={ziActiva || false}
                onChange={(e, v) => setZiActiva(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 1, borderBottom: 1, borderColor: "divider", minHeight: 40 }}
            >
                {zile.map((z) => (
                    <Tab
                        key={z.data}
                        value={z.data}
                        sx={{ minHeight: 40, textTransform: "none" }}
                        label={`${zileConfirmate.has(z.data) ? "✅ " : ""}${etichetaZi(z.data)}`}
                    />
                ))}
            </Tabs>

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Colaborator</TableCell>
                        <TableCell>Rol</TableCell>
                        <TableCell align="right">Ore confirmate</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {echipeIds.map((echipaId) => {
                        const membrii = colaboratori.filter(
                            (c) => extractId(c.echipa) === echipaId
                        );
                        if (!membrii.length) return null;

                        const numeEchipa = echipeMap[echipaId] || `Echipa #${echipaId}`;

                        return (
                            <React.Fragment key={echipaId}>
                                {/* Rand separator echipa */}
                                <TableRow>
                                    <TableCell
                                        colSpan={3}
                                        sx={{
                                            bgcolor: "action.hover",
                                            py: 0.5,
                                            fontWeight: 700,
                                            fontSize: "0.8em",
                                            color: "text.secondary",
                                            letterSpacing: 0.5,
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {numeEchipa}
                                    </TableCell>
                                </TableRow>
                                {membrii.map((c) => {
                                    const areConfirmare = confirmariZiActiva.some(
                                        // eslint-disable-next-line eqeqeq
                                        (o) => extractId(o.colaborator) == c.id
                                    );
                                    const editabil = confirmatDeId && colaboratoriPermisiIds.has(c.id);
                                    return (
                                        <React.Fragment key={c.id}>
                                        <TableRow sx={{ opacity: confirmatDeId && !editabil ? 0.4 : 1 }}>
                                            <TableCell>
                                                {c.nume} {c.prenume}
                                                {!areConfirmare && confirmariZiActiva.length > 0 && (
                                                    <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                                                        (nou)
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">
                                                    {c.rol || "-"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={oreZiActiva[c.id] ?? ""}
                                                    onChange={(e) => handleOreChange(ziActiva, c.id, e.target.value)}
                                                    sx={{ width: 100 }}
                                                    inputProps={{
                                                        step: 0.5,
                                                        min: 0,
                                                        style: {
                                                            borderColor: areConfirmare ? undefined : '#ef6c00',
                                                            outline: areConfirmare ? undefined : '2px solid #ef6c00',
                                                            borderRadius: 4,
                                                        }
                                                    }}
                                                    disabled={saving || !editabil}
                                                />
                                            </TableCell>
                                        </TableRow>
                                        {/* Nota pe rand separat, pe toata latimea: o a patra
                                            coloana ar fi facut tabelul nefolosibil pe telefon. */}
                                        <TableRow sx={{ opacity: confirmatDeId && !editabil ? 0.4 : 1 }}>
                                            <TableCell colSpan={3} sx={{ pt: 0, borderBottom: "1px solid rgba(128,128,128,0.2)" }}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    multiline
                                                    minRows={1}
                                                    maxRows={4}
                                                    placeholder="Detalii (optional): ce a lucrat, ce a lipsit, de ce a durat mai mult"
                                                    value={detaliiZiActiva[c.id] ?? ""}
                                                    onChange={(e) => handleDetaliiChange(ziActiva, c.id, e.target.value)}
                                                    disabled={saving || !editabil}
                                                    inputProps={{ maxLength: 2000 }}
                                                    variant="standard"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Button
                    variant="contained"
                    onClick={handleConfirmaZiua}
                    disabled={saving || !confirmatDeId || !colaboratoriPermisi.length || !ziActiva}
                >
                    {saving ? "Se salveaza..." : `Confirma ${etichetaZi(ziActiva)}`}
                </Button>
                {zile.length > 1 && (
                    <Button
                        variant="outlined"
                        onClick={handleConfirmaTot}
                        disabled={saving || !confirmatDeId || !colaboratoriPermisi.length}
                    >
                        Confirma toate cele {zile.length} zile
                    </Button>
                )}
            </Box>
            {zile.length > 1 && !evenimentTerminat && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Evenimentul nu s-a incheiat inca - zilele scrise acum raman corectabile individual.
                </Typography>
            )}
        </Box>
    );
};

// Memoizat: parintele (EvenimenteCalendar) se re-randeaza la fiecare tasta din
// dialogul de editare. Fara memo, toata sectiunea de confirmare ore - inclusiv
// tabelul de colaboratori - s-ar re-randa degeaba la fiecare caracter.
export const ConfirmareOre = React.memo(ConfirmareOreBase);
